import React from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';

const AboutUs = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="grow container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-16">
          {/* Hero Section */}
          <div className="text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              About Light Hunt
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Lighting the way to better displays—together.
            </p>
          </div>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Our Story */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
              <p>
                As a longtime holiday light show enthusiast using Light-O-Rama and xLights, our founder faced a familiar challenge: finding and comparing sequences for a favorite song often meant digging through forums and emailing strangers, hoping for a response. There was no central place to see how different creators interpreted the same track—no easy way to preview, compare, or discover what might light up your neighborhood next.
              </p>
              <p>
                Light Hunt was born from that frustration—built by someone who's been in your shoes and wanted a better way. What started as a simple idea grew into a platform designed to bring display owners and creative sequencers together in a more open, collaborative way.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Our Mission */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold text-center">Our Mission</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
              <p>
                To help creators and display owners easily find, share, and compare light sequences for songs they love.
              </p>
              <p>
                Whether you're syncing your first song or curating a massive multi-house display, Light Hunt gives you the tools to discover new interpretations, revisit classics, and build your show with confidence.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Our Vision */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold text-center">Our Vision</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
              <p>
                We believe in a world where holiday displays are more than just lights—they're stories, shared through rhythm and glow.
              </p>
              <p>
                Our vision is to build a community-driven ecosystem where high-quality synchronized light shows can be celebrated, shared, and continually improved. By making it easier to find and compare sequences, we aim to foster creativity and connection in displays everywhere—from small-town lawns to national showcases.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Why We Exist */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold text-center">Why We Exist</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
              <p>
                Every sequencer sees a song differently—and that's the magic. Light Hunt exists to make it easy to compare those interpretations side by side, giving display owners the freedom to choose the style that fits their vision best.
              </p>
              <p>
                We want to remove the barriers to discovery, inspire more collaboration, and ultimately help the whole community grow. The more accessible we make synchronized sequencing, the brighter the season gets for everyone.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Our Team */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold text-center">Our Team</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
              <p>
                We're not here to be the face of Light Hunt—you are. Our team is built around community-first values: support over spotlight, utility over ego, and listening before launching.
              </p>
              <p>
                Whether you're a seasoned sequencer or just putting up your first pixels, you're part of the reason this platform exists. We're here to serve that spirit of collaboration—and to keep building what you need next.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="flex justify-center">
            <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Call to Action */}
          <section className="text-center space-y-6 bg-muted/50 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold">Join the Hunt. Light the Season.</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're just getting started, and there's room for every creator and every display to shine.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
              <a 
                href="/displays" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Explore Displays
              </a>
              <a 
                href="/sequences" 
                className="inline-flex items-center px-6 py-3 rounded-lg border border-border bg-background text-foreground font-medium hover:bg-muted transition-colors"
              >
                Browse Sequences
              </a>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;